import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Star, Trash2 } from "lucide-react";

export default function ReviewsManager() {
  const [items, setItems] = useState([]);
  const [author, setAuthor] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [date, setDate] = useState("");

  const load = async () => {
    const { data } = await api.get("/reviews");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/reviews", { author, rating: Number(rating), text, date });
      toast.success("Bewertung hinzugefügt");
      setAuthor(""); setRating(5); setText(""); setDate("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bewertung löschen?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Gelöscht");
    load();
  };

  return (
    <div data-testid="reviews-manager" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={handleSubmit} className="lg:col-span-1 bg-white border border-sand-300 p-6 rounded-sm space-y-4 h-fit">
        <h3 className="font-display text-2xl text-stone-900">Neue Bewertung</h3>
        <div>
          <Label className="mb-2 block">Name *</Label>
          <Input required value={author} onChange={(e) => setAuthor(e.target.value)} data-testid="review-author-input" />
        </div>
        <div>
          <Label className="mb-2 block">Bewertung (1–5) *</Label>
          <Input type="number" min="1" max="5" value={rating} onChange={(e) => setRating(e.target.value)} data-testid="review-rating-input" />
        </div>
        <div>
          <Label className="mb-2 block">Datum (optional)</Label>
          <Input placeholder="z.B. März 2025" value={date} onChange={(e) => setDate(e.target.value)} data-testid="review-date-input" />
        </div>
        <div>
          <Label className="mb-2 block">Text *</Label>
          <Textarea rows={5} required value={text} onChange={(e) => setText(e.target.value)} data-testid="review-text-input" />
        </div>
        <Button type="submit" data-testid="review-submit" className="w-full bg-stone-900 hover:bg-clay text-sand-100 rounded-sm">
          <Plus className="w-4 h-4 mr-2" /> Hinzufügen
        </Button>
      </form>

      <div className="lg:col-span-2">
        <h3 className="font-display text-2xl text-stone-900 mb-4">Bewertungen ({items.length})</h3>
        {items.length === 0 ? (
          <p className="text-stone-600 italic" data-testid="reviews-empty">Noch keine Bewertungen.</p>
        ) : (
          <div className="space-y-4">
            {items.map((r) => (
              <div key={r.id} data-testid={`review-row-${r.id}`} className="bg-white border border-sand-300 rounded-sm p-5 flex justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <p className="font-medium text-stone-900">{r.author}</p>
                    <div className="flex">{Array.from({length: r.rating}).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-clay text-clay" />)}</div>
                    {r.date && <p className="text-xs text-stone-600">{r.date}</p>}
                  </div>
                  <p className="text-stone-700 text-sm">{r.text}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)} data-testid={`review-delete-${r.id}`} className="text-stone-700 hover:text-clay hover:bg-sand-200 shrink-0">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
